import "mapbox-gl/dist/mapbox-gl.css";
import Logo from "../../images/Logo.png";
import classes from "./OrderInformation.module.css";
import ProgressFlow from "./progressFlow/ProgressFlow";
import packageIcon from "../../images/orderInformation/packageIcon.svg";
import downloadIcon from "../../images/orderInformation/downloadIcon.svg";
import Map, { Marker } from "react-map-gl";
import { ArrowBtn } from "../buttons/arrowBtn/ArrowBtn";
import { useLocation } from "react-router-dom";
import { LoadingSpinner } from "../loadingSpinner/LoadingSpinner";
import { useTranslations } from "../../helperFunctions/customHooks/useTranslations";
import { downloadDeliveryA4 } from "../../helperFunctions/deliveryExport2";
import { fetchDriverLocation } from "../../redux/driver/thunks/getDriverLocation";
import { fetchPackageProgress } from "../../redux/package/getPackageProgress";
import { setSelectedOrderIndex } from "../../redux/globalFunctions/globalFnSlice";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState, useRef } from "react";

// Cache for geocoding results
const geocodeCache = {};

export function OrderInformation() {
  const dispatch = useDispatch();
  const [coordinates, setCoordinates] = useState(null);
  const [isMapInitialized, setIsMapInitialized] = useState(false);
  const mapRef = useRef(null);
  const retryCountRef = useRef(0);
  const MAX_RETRIES = 3;
  const contentRef = useRef(null);
  const token = useSelector((s) => s.user.token);
  const { t } = useTranslations();

  const location = useLocation();

  const { progress, status: progressStatus } = useSelector(
    (state) => state.getPackageProgress
  );

  const { selectedOrderIndex, deliveriesToDisplay } = useSelector(
    (state) => state.global
  );

  const driverLocations = useSelector(
    (state) => state.driverLocation.locations
  );

  const [shippingStatus, setShippingStatus] = useState("");
  const [driverId, setDriverId] = useState("");
  const [address, setAddress] = useState("");
  const [pickupAddress, setPickupAddress] = useState("");
  const [isPdfLoading, setIsPdfLoading] = useState(false);

  function handlePdfLoading() {
    setIsPdfLoading(true);
    setTimeout(() => {
      setIsPdfLoading(false);
    }, 200);
  }

  useEffect(() => {
    if (deliveriesToDisplay) {
      setDriverId(
        deliveriesToDisplay[selectedOrderIndex].driver?.id ||
          deliveriesToDisplay[selectedOrderIndex].driver_id ||
          deliveriesToDisplay[selectedOrderIndex].driver_name
      );
      setAddress(deliveriesToDisplay[selectedOrderIndex].dropoff_address_name);
      setPickupAddress(
        deliveriesToDisplay[selectedOrderIndex].pickup_address_name
      );
    }
  }, [deliveriesToDisplay, selectedOrderIndex]);

  useEffect(() => {
    const packageId =
      deliveriesToDisplay[selectedOrderIndex].package_id ||
      deliveriesToDisplay[selectedOrderIndex].delivery_id;
    dispatch(fetchPackageProgress(packageId));
  }, [selectedOrderIndex, deliveriesToDisplay]);

  useEffect(() => {
    if (progressStatus === "success") {
      setShippingStatus(
        progress.length !== 0 ? progress[progress.length - 1].status_name : ""
      );
    }
  }, [progressStatus]);

  const isFirst = selectedOrderIndex === 0;
  const isLast = selectedOrderIndex === deliveriesToDisplay?.length - 1;
  const hasOnlyOneDelivery = deliveriesToDisplay?.length === 1;

  const handlePrevDelivery = () => {
    if (selectedOrderIndex > 0) {
      dispatch(setSelectedOrderIndex(selectedOrderIndex - 1));
    }
  };

  const handleNextDelivery = () => {
    if (selectedOrderIndex < deliveriesToDisplay.length - 1) {
      dispatch(setSelectedOrderIndex(selectedOrderIndex + 1));
    }
  };

  const mapboxToken =
    "pk.eyJ1IjoiYm96aWRhcmszNSIsImEiOiJjbTFxNTdzMWUwOWZwMmpzYjN4eHF5Zmt5In0.fVeom9WzttFClBuhzNSjKg";

  // Geocode a location and cache the result - memoized with useCallback
  async function geocodeLocation(locationStr) {
    if (!locationStr) return null;

    // Check cache first
    if (geocodeCache[locationStr]) {
      return geocodeCache[locationStr];
    }

    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
          locationStr
        )}.json?access_token=${mapboxToken}`
      );

      if (!response.ok) {
        throw new Error(`Geocoding API error: ${response.status}`);
      }

      const data = await response.json();

      if (data.features && data.features.length > 0) {
        const [longitude, latitude] = data.features[0].center;
        const result = { latitude, longitude };

        geocodeCache[locationStr] = result; // cache it
        return result;
      } else {
        console.warn("No features found for location:", locationStr);
      }
    } catch (error) {
      console.error("Error geocoding location:", locationStr, error);
    }
    return null;
  }

  // Fetch driver locations once on mount
  useEffect(() => {
    dispatch(fetchDriverLocation({}));
  }, [dispatch]);

  // Function to update map location - extracted for reuse
  async function updateMapLocation() {
    if (retryCountRef.current >= MAX_RETRIES) return;

    if (progressStatus === "loading") return;

    let targetCoordinates = null;

    if (shippingStatus === "Picked" && pickupAddress) {
      targetCoordinates = await geocodeLocation(pickupAddress);
    } else if (shippingStatus === "Delivered" && address) {
      targetCoordinates = await geocodeLocation(address);
    } else {
      if (driverId && driverLocations?.length > 0) {
        const driver = driverLocations.find(
          (driver) =>
            String(driver.dr_uid) === String(driverId) ||
            driver.u_firstname + " " + driver.u_lastname === driverId
        );
        if (driver?.dr_current_location) {
          targetCoordinates = await geocodeLocation(driver.dr_current_location);
        }
      }

      if (!targetCoordinates && address) {
        targetCoordinates = await geocodeLocation(address);
      }
    }

    if (targetCoordinates) {
      setCoordinates(targetCoordinates);

      if (mapRef.current && isMapInitialized) {
        try {
          mapRef.current.flyTo({
            center: [targetCoordinates.longitude, targetCoordinates.latitude],
            zoom: 14,
            duration: 1000,
          });
        } catch (error) {
          console.error("Error flying to location:", error);
        }
      }
    }
  }

  // Update the map when selectedOrderIndex changes (but don't remount the map)
  useEffect(() => {
    // Update location if map is already initialized
    if (isMapInitialized) {
      updateMapLocation();
    }
  }, [selectedOrderIndex, isMapInitialized, updateMapLocation]);

  // Handle initial map load and location update
  function handleMapLoad() {
    setIsMapInitialized(true);
    if (coordinates && mapRef.current) {
      try {
        mapRef.current.flyTo({
          center: [coordinates.longitude, coordinates.latitude],
          zoom: 14,
          duration: 1000,
        });
      } catch (error) {
        console.error("Error flying to location on map load:", error);
      }
    } else {
      updateMapLocation();
    }
  }

  // Initial view state is only set once when the map is first created
  // This prevents the map from re-initializing when state changes
  const initialViewState = {
    latitude: 51.505, // Default central view
    longitude: -0.09,
    zoom: 3,
  };

  const text = t.orderInformation;

  return (
    <div
      className={`${classes.orderInfoContainer} fadeInTranslate`}
      style={{
        height: location.pathname === "/deliveries" ? "98dvh" : "",
        width: location.pathname === "/deliveries" ? "500px" : "",
      }}
    >
      <div ref={contentRef}>
        <div className={classes.firstDiv}>
          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <div className={classes.imgContainer}>
              <img src={packageIcon} alt="Icon" />
            </div>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "4px" }}
            >
              <h6>
                {text.driver}{" "}
                {deliveriesToDisplay[selectedOrderIndex || 0].driver_name ||
                  deliveriesToDisplay[selectedOrderIndex || 0].driver?.name ||
                  ""}
              </h6>
              <p>
                {text.order}{" "}
                {deliveriesToDisplay[selectedOrderIndex].delivery_id ??
                  deliveriesToDisplay[selectedOrderIndex].package_id}
              </p>
            </div>
          </div>
          <div className={classes.navButtons}>
            <ArrowBtn
              disabled={isFirst || hasOnlyOneDelivery}
              onClick={handlePrevDelivery}
              direction={"left"}
            />
            <ArrowBtn
              onClick={handleNextDelivery}
              disabled={isLast || hasOnlyOneDelivery}
              direction={"right"}
            />
          </div>
        </div>

        <div>
          <div className={classes.secondDiv}>
            {/* Map is only created once and maintained throughout component lifecycle */}
            <div
              className="map-export"
              style={{ width: "100%", height: "100%" }}
            >
              <Map
                ref={mapRef}
                initialViewState={initialViewState}
                mapStyle="mapbox://styles/mapbox/streets-v11"
                mapboxAccessToken={mapboxToken}
                style={{ borderRadius: "8px" }}
                dragRotate={false}
                attributionControl={false}
                onLoad={handleMapLoad}
                reuseMaps
                preserveDrawingBuffer
              >
                {coordinates && (
                  <Marker
                    key={`marker-${address || driverId}-${selectedOrderIndex}`}
                    latitude={coordinates.latitude}
                    longitude={coordinates.longitude}
                    color="#1E88E5"
                  />
                )}
              </Map>
            </div>
          </div>

          <div className={classes.orderInfoContainerBottom}>
            <div className={classes.orderInformationDiv}>
              <div>
                <h6>{text.headerText}</h6>
              </div>
              <div className={classes.orderInfoItem}>
                <p>{text.date}</p>
                <span>
                  {deliveriesToDisplay[selectedOrderIndex].order_date}
                </span>
              </div>
              <div className={classes.orderInfoItem}>
                <p>{text.customer}:</p>
                <span
                  title={
                    deliveriesToDisplay[selectedOrderIndex]?.customer?.name ||
                    deliveriesToDisplay[selectedOrderIndex]?.customer_name ||
                    ""
                  }
                >
                  {deliveriesToDisplay[selectedOrderIndex]?.customer?.name ||
                    deliveriesToDisplay[selectedOrderIndex]?.customer_name ||
                    ""}
                </span>
              </div>
              <div className={classes.orderInfoItem}>
                <p>{text.address}</p>
                <span title={address}>{address}</span>
              </div>
            </div>

            <div className={classes.thirdDiv}>
              {progressStatus === "success" && (
                <button
                  className={classes.downloadBtn}
                  // onClick={() =>
                  // downloadOrderPdf({
                  //   contentEl: contentRef.current,
                  //   packageId:
                  //     deliveriesToDisplay[selectedOrderIndex].package_id ||
                  //     deliveriesToDisplay[selectedOrderIndex].delivery_id,
                  //   authToken: token,
                  //   fileName: `order_${
                  //     deliveriesToDisplay[selectedOrderIndex].package_id ||
                  //     deliveriesToDisplay[selectedOrderIndex].delivery_id
                  //   }.pdf`,
                  //   mapRef: mapRef,
                  //   mapboxToken: mapboxToken,
                  //   markerLngLat: coordinates
                  //     ? [coordinates.longitude, coordinates.latitude]
                  //     : undefined,
                  // })

                  // downloadMapAndInfoPdf({
                  //   mapRef,
                  //   mapboxToken,
                  //   markerLngLat: coordinates
                  //     ? [coordinates.longitude, coordinates.latitude]
                  //     : undefined,
                  //   mapX: 10,
                  //   mapY: 10,
                  //   mapWidth: 99,
                  //   mapHeight: 55,
                  //   infoEl: document.getElementsByClassName(classes.thirdDiv)[0],
                  //   infoX: 10,
                  //   infoY: 120,
                  //   infoWidth: 190,
                  //   fileName: "order-details.pdf",
                  //   hideElement: document.getElementsByClassName(classes.downloadBtn)[0],
                  // })

                  // }

                  onClick={async () => {
                    await downloadDeliveryA4({
                      coordinates,
                      mapRef,
                      mapboxToken,
                      markerLngLat: coordinates
                        ? [coordinates.longitude, coordinates.latitude]
                        : undefined,
                      infoEl: document.getElementsByClassName(
                        classes.thirdDiv
                      )[0], // or ref to your statuses div
                      hideElement: document.getElementsByClassName(
                        classes.downloadBtn
                      )[0],

                      orderNumber:
                        deliveriesToDisplay[selectedOrderIndex].delivery_id ??
                        deliveriesToDisplay[selectedOrderIndex].package_id,
                      driverName:
                        deliveriesToDisplay[selectedOrderIndex]?.driver?.name ||
                        deliveriesToDisplay[selectedOrderIndex]?.driver_name,
                      orderDate:
                        deliveriesToDisplay[selectedOrderIndex].order_date,
                      customerName:
                        deliveriesToDisplay[selectedOrderIndex]?.customer
                          ?.name ||
                        deliveriesToDisplay[selectedOrderIndex]
                          ?.customer_name ||
                        "",
                      pickupAddress:
                        deliveriesToDisplay[selectedOrderIndex]
                          .pickup_address_name,
                      dropoffAddress:
                        deliveriesToDisplay[selectedOrderIndex]
                          .dropoff_address_name,

                      packageId:
                        deliveriesToDisplay[selectedOrderIndex].delivery_id ??
                        deliveriesToDisplay[selectedOrderIndex].package_id,
                      authToken: token,

                      logoSrc: Logo, // e.g. `logo` imported PNG or base64 string
                      brand: { primary: "#121B3F", muted: "#676C7E" },

                      // optional fonts if you embed them
                      // fontSemiBoldBase64: openSans600Base64,
                      // fontRegularBase64: openSansRegularBase64,

                      fileName: "delivery.pdf",
                    });
                    handlePdfLoading();
                  }}
                >
                  {isPdfLoading ? (
                    <LoadingSpinner black={true} />
                  ) : (
                    <img src={downloadIcon} alt="" />
                  )}
                </button>
              )}

              {progressStatus !== "loading" && (
                <ProgressFlow progress={progress} errorMsg={text.errorMsg} />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
